import { useState } from 'react';

import { useLanguage } from '../contexts/LanguageContext';

/**
 * Un message d'écran qui se traduit AU RENDU, jamais à l'instant de l'erreur.
 *
 * ── LE DÉFAUT QU'IL FERME, MESURÉ AU LOT RU5-e ──────────────────────────────
 * 🔴 `setError(t('messages.productLoadError'))` FIGE LA TRADUCTION. Le
 * dictionnaire est chargé par un `fetch` dans `LanguageContext` : `translations`
 * démarre à `{}`. Un appel API qui échoue **avant** que le dictionnaire ait
 * répondu fait rendre à `t()` la **CLÉ BRUTE** — et cette clé reste **gelée
 * dans l'état**, que le dictionnaire arrive ensuite ou non. *L'écran affiche
 * `messages.productLoadError` en toutes lettres.*
 *
 * ⚠️ ET LE MÊME DÉFAUT SE REJOUE AU CHANGEMENT DE LANGUE : une erreur posée en
 * français reste en français après un passage à l'hébreu. Deux symptômes, une
 * seule cause — *une chaîne déjà traduite ne repasse jamais par `t()`.*
 *
 * ── POURQUOI UN HOOK, ET NON LA FORME DU LOGIN ──────────────────────────────
 * 🔴 LE LOGIN STOCKE `{ cle }` ET TRADUIT À L'ENDROIT DU RENDU. C'était juste
 * pour **deux** points de lecture. **Mesuré ici : les onze écrans concernés
 * comptent ~82 points de lecture** — `{error && <Alert>{error}</Alert>}`,
 * `disabled={Boolean(error)}`, `error ? … : …`. Les convertir un par un aurait
 * touché quatre-vingt-deux endroits pour douze écritures, *et le premier oublié
 * aurait affiché `[object Object]`.*
 *
 * ⚠️ **LE HOOK REND UNE CHAÎNE DÉJÀ TRADUITE : AUCUN POINT DE LECTURE NE
 * CHANGE.** Seule la déclaration change (`useState` → `useMessageTraduit`) et
 * l'écriture prend une clé au lieu d'une chaîne. *Le correctif qui ne crée pas
 * de dette est celui qui ne change pas de forme* — leçon du lot RU8-b, sur la
 * même famille de problème.
 *
 * ── CE QU'IL ACCEPTE ────────────────────────────────────────────────────────
 *   poser(cle('messages.x'))              → traduit au rendu
 *   poser(cle('cart.addedCount', { n }))  → …avec ses variables
 *   poser(texte(err.response.data.error)) → un message du SERVEUR, jamais traduit
 *   poser(null)                           → efface
 *
 * 🔴 LES DEUX NATURES SONT SÉPARÉES, ET C'EST LE CŒUR. Un message venu du
 * serveur est **déjà en clair** : le passer à `t()` rendrait la phrase
 * elle-même comme une clé introuvable. *Les confondre en une seule chaîne est
 * exactement ce qui a créé le défaut du Login.*
 */

/** Un message à traduire au rendu. `params` porte les variables du gabarit. */
export const cle = (identifiant, params) => ({ cle: identifiant, params });

/** Un message DÉJÀ en clair — typiquement rendu par le serveur. */
export const texte = (contenu) => ({ texte: contenu });

/**
 * Le message d'un serveur, avec un repli traduit s'il n'a rien dit.
 *
 * ⚠️ L'ORDRE EST UNE PRIORITÉ : le serveur sait POURQUOI (« fichier trop
 * volumineux », « type non autorisé »), le repli ne sait que QUE. *Un libellé
 * générique préféré au message du serveur perd la seule information utile.*
 */
export const duServeur = (erreur, cleDeRepli, params) => {
  const message = erreur?.response?.data?.error;
  return message ? texte(message) : cle(cleDeRepli, params);
};

/**
 * `[message, poser]` — `message` est une CHAÎNE déjà traduite, prête à rendre.
 *
 * ⚠️ LA TRADUCTION EST FAITE À CHAQUE RENDU, pas mémoïsée : `t` change
 * d'identité quand le dictionnaire arrive et quand la langue change — c'est
 * précisément à ces deux instants que le message doit être retraduit. *Mémoïser
 * ici rétablirait le gel qu'on vient de retirer.*
 */
export function useMessageTraduit(initial = null) {
  const { t } = useLanguage();
  const [descripteur, setDescripteur] = useState(initial);

  const message = !descripteur ? ''
    : (descripteur.cle ? t(descripteur.cle, descripteur.params) : (descripteur.texte || ''));

  return [message, setDescripteur];
}
